import {
  ChangeDetectionStrategy,
  EventEmitter, Inject, Injector, INJECTOR,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { Component, Input, ViewChild } from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import {CHECK_LAYOUT_DIRECTION_PROVIDER} from "../../providers";
import {IntDefaultInputField} from "../fields";
import {TVerticalAlign} from "../../types";


@Component({
  selector: 'int-textarea',
  templateUrl: 'int-textarea.component.html',
  styleUrls: ['int-textarea-vars.component.scss', 'int-textarea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntTextareaComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntTextareaComponent implements ControlValueAccessor, OnInit
{
  value$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  dir$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>('ltr');

  @Input()
  set value(value) {
    this.value$.next(value);
  }

  @Input()
  set direction(value: string) {
    if(value) {
      this.dir$.next(value);
    }else{
      this.dir$.next(this.directionLayout.value)
    }
  }

  @Input()
  placeholder = '';

  @Input()
  label = '';

  @Input() defaultHeight = '3rem';

  @Input() fieldHeight = this.defaultHeight;

  @Input()
  showLabelTip = true;

  @Input()
  readonly = false;

  @Input()
  verticalAlign: TVerticalAlign = 'top';

  @Output()
  changeValueEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  onBlurEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  onFocusEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  disabled = false;
  touched = false;
  focused = false;

  control!: NgControl;

  @ViewChild(IntDefaultInputField) _field!: IntDefaultInputField;

  constructor(
    private readonly renderer2: Renderer2,
    @Inject(CHECK_LAYOUT_DIRECTION_PROVIDER)
    private readonly directionLayout: BehaviorSubject<string>,
    @Inject(INJECTOR) private  readonly injector: Injector
  ) {}

  ngOnInit() {
    this.setControl();
  }

  get directionLayoutValue(): string {
    return this.directionLayout.value;
  }

  get currentLabel(): string {
    return this.label && (this.value || this.focused)
      ? this.label
      : this.placeholder || this.label;
  }

  get isNotLabelOrPlaceholder(): boolean {
    return !(Boolean(this.label) || Boolean(this.placeholder));
  }

  get isError(): boolean {
    if(this.control) {
      return Boolean(this.control?.invalid) && Boolean(this.control?.dirty)
    }
    return false;
  }

  get isFill() {
    return this.value.length > 0;
  }

  get isFocused() {
    return this.focused;
  }

  get value(): string {
    return this.value$.value || '';
  }

  setControl() {
    try {
      this.control = this.injector.get(NgControl);
    }catch(err) {
      console.log('please provide NgControl for [IntTextareaComponent]');
    }
  }

  writeValue(value: string | null) {
    this.value$.next(value ?? '');
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  changeValue(event: Event) {
    const target = event.target as HTMLInputElement;
    this.value$.next(target.value);
    this.onChange(target.value);
    this.changeValueEvent.emit(this.value);
  }

  onChange(val: any) {}

  onTouched() {}

  onInput(value: string) {
    this.onChange(value);
    this.value$.next(value);
  }

  onFocus() {
    this.focused = true;
    this.onFocusEvent.emit(true);
    this.onBlurEvent.emit(false);
  }
  onBlur() {
    this.focused = false;
    this.onFocusEvent.emit(false);
    this.onBlurEvent.emit(true);
  }
}
